// ============================================================================
// TRIANGLE D'OR (GOLDEN TRIANGLE) TYPES
// ============================================================================

/**
 * Status levels for triangle metrics
 */
export type MetricStatus = 'good' | 'warning' | 'critical';

/**
 * Balance types for the project triangle
 */
export type TriangleBalance =
  | 'optimal'           // All three dimensions are balanced
  | 'cost-focused'      // Prioritizing budget over time/quality
  | 'time-focused'      // Prioritizing schedule over cost/quality
  | 'quality-focused';  // Prioritizing quality over cost/time

/**
 * Alert types for the golden triangle
 */
export interface TriangleAlert {
  id: string;
  type: 'info' | 'warning' | 'critical';
  dimension: 'cost' | 'time' | 'quality';
  message: string;
  recommendation?: string;
  timestamp: Date;
}

/**
 * Cost dimension of the triangle
 */
export interface CostMetric {
  value: number;              // Percentage (0-100)
  budgetTotal: number;        // Total project budget
  budgetUsed: number;         // Budget consumed so far
  budgetRemaining: number;    // Remaining budget
  variance: number;           // Budget variance (positive = under budget)
  status: MetricStatus;
}

/**
 * Time dimension of the triangle
 */
export interface TimeMetric {
  value: number;              // Percentage (0-100)
  totalDuration: number;      // Total project duration in days
  elapsedDuration: number;    // Days elapsed since start
  remainingDuration: number;  // Days remaining
  variance: number;           // Schedule variance (positive = ahead of schedule)
  status: MetricStatus;

  // Calculated from project
  plannedStartDate: Date | null;
  plannedEndDate: Date | null;
  currentDate: Date;
  projectedEndDate: Date | null;
}

/**
 * Quality/Scope dimension of the triangle
 */
export interface QualityMetric {
  value: number;              // Overall quality score (0-100)
  completedTasks: number;     // Number of completed tasks
  totalTasks: number;         // Total number of tasks
  criticalTasks: number;      // Number of critical path tasks
  criticalTasksCompleted: number;
  defects: number;            // Number of issues/defects
  status: MetricStatus;
}

/**
 * Main Golden Triangle interface
 */
export interface GoldenTriangle {
  id: string;
  projectId: string;

  // Three dimensions
  cost: CostMetric;
  time: TimeMetric;
  quality: QualityMetric;

  // Overall analysis
  balance: TriangleBalance;
  overallHealth: number;      // Composite health score (0-100)
  alerts: TriangleAlert[];
  recommendations: string[];

  // Metadata
  lastCalculated: Date;
  autoUpdate: boolean;
}

/**
 * Configuration for triangle calculations
 */
export interface TriangleConfig {
  // Thresholds for status levels
  costWarningThreshold: number;      // % of budget used to trigger warning
  costCriticalThreshold: number;     // % of budget used to trigger critical

  timeWarningThreshold: number;      // % of time elapsed vs progress to trigger warning
  timeCriticalThreshold: number;     // % of time elapsed vs progress to trigger critical

  qualityWarningThreshold: number;   // Minimum quality score for warning
  qualityCriticalThreshold: number;  // Minimum quality score for critical

  // Weights for overall health calculation (should sum to 1)
  costWeight: number;
  timeWeight: number;
  qualityWeight: number;
}

/**
 * Default configuration
 */
export const DEFAULT_TRIANGLE_CONFIG: TriangleConfig = {
  costWarningThreshold: 80,
  costCriticalThreshold: 95,

  timeWarningThreshold: 75,
  timeCriticalThreshold: 90,

  qualityWarningThreshold: 70,
  qualityCriticalThreshold: 50,

  costWeight: 0.33,
  timeWeight: 0.34,
  qualityWeight: 0.33,
};

/**
 * Historical snapshot for tracking triangle evolution
 */
export interface TriangleSnapshot {
  id: string;
  projectId: string;
  timestamp: Date;
  cost: number;
  time: number;
  quality: number;
  balance: TriangleBalance;
  overallHealth: number;
}
